/**
 * Indian-locale number formatting (ported verbatim from the mobile app).
 * Indian grouping (₹2,00,000 not ₹200,000), U+2212 minus, tabular figures.
 */

export const NO_VALUE = '—';

const bad = (v: number): boolean => !Number.isFinite(v);

/** Group an integer part with Indian digit grouping: 200000 -> "2,00,000". */
function groupIndian(intStr: string): string {
  if (intStr.length <= 3) return intStr;
  const last3 = intStr.slice(-3);
  const rest = intStr.slice(0, -3);
  return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
}

/** Format a rupee amount, e.g. 74776.75 -> "₹74,776.75". Sign optional. */
export function rupee(
  amount: number,
  opts: { decimals?: number; sign?: boolean; symbol?: boolean } = {},
): string {
  if (bad(amount)) return NO_VALUE;
  const { decimals = 2, sign = false, symbol = true } = opts;
  const abs = Math.abs(amount);
  const fixed = abs.toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  let out = groupIndian(intPart!);
  if (decPart) out += '.' + decPart;
  if (symbol) out = '₹' + out;
  if (sign) out = (amount < 0 ? '−' : '+') + out;
  else if (amount < 0) out = '−' + out;
  return out;
}

/** Whole-rupee format, no decimals: 200000 -> "₹2,00,000". */
export function rupee0(amount: number, opts: { sign?: boolean } = {}): string {
  return rupee(amount, { decimals: 0, ...opts });
}

/** Plain number with Indian grouping: 22453.3 -> "22,453.30". */
export function num(value: number, decimals = 2): string {
  if (bad(value)) return NO_VALUE;
  const abs = Math.abs(value);
  const [intPart, decPart] = abs.toFixed(decimals).split('.');
  let out = groupIndian(intPart!);
  if (decPart) out += '.' + decPart;
  return (value < 0 ? '−' : '') + out;
}

/** Percentage from a fraction: 0.0065 -> "+0.65%". */
export function pct(fraction: number, opts: { sign?: boolean; decimals?: number } = {}): string {
  if (bad(fraction)) return NO_VALUE;
  const { sign = true, decimals = 2 } = opts;
  const v = fraction * 100;
  const body = Math.abs(v).toFixed(decimals) + '%';
  if (sign) return (v < 0 ? '−' : '+') + body;
  return (v < 0 ? '−' : '') + body;
}

/** Indian compact rupees: 200000 → "₹2L", 24500000 → "₹2.45Cr". */
export function rupeeCompact(amount: number, opts: { sign?: boolean } = {}): string {
  if (bad(amount)) return NO_VALUE;
  const abs = Math.abs(amount);
  if (abs < 1e5) return rupee0(amount, opts);
  let [value, suffix] = abs >= 1e7 ? [abs / 1e7, 'Cr'] : [abs / 1e5, 'L'];
  if (suffix === 'L' && Number((value as number).toFixed(2)) >= 100) [value, suffix] = [abs / 1e7, 'Cr'];
  let out = '₹' + (value as number).toFixed(2).replace(/\.?0+$/, '') + suffix;
  if (opts.sign) out = (amount < 0 ? '−' : '+') + out;
  else if (amount < 0) out = '−' + out;
  return out;
}

/** Compact counts: 12480 -> "12,480", 8200 -> "8.2k". */
export function compact(value: number): string {
  if (bad(value)) return NO_VALUE;
  if (value < 1000) return String(value);
  if (value < 100000) return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return groupIndian(String(value));
}

/** "14:03" from epoch ms. */
export function hhmm(at: number): string {
  const d = new Date(at);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * The IST wall-clock time out of the API's `serverTimeIst`.
 *
 * That field is an IST-shifted Date serialised with `toISOString()`, so it
 * arrives as "2026-09-04T21:28:33.260Z" — carrying an IST time under a Z
 * that says UTC. Printed raw next to the word "IST", as it was in the tape
 * clock and the desk header, it is both unreadable and self-contradictory.
 *
 * The wall-clock reading is already in the string, so this takes it from
 * there rather than re-parsing into a Date and re-applying an offset — a
 * round trip that would shift it a second time in any non-IST browser.
 */
export function istClock(iso: string): string {
  const m = /T(\d{2}:\d{2})/.exec(iso);
  return m ? m[1]! : iso;
}

export function hhmmss(at: number): string {
  const d = new Date(at);
  return `${hhmm(at)}:${String(d.getSeconds()).padStart(2, '0')}`;
}
